<template>
  <div class="invoice-monitor-tab">
    <!-- 顶部操作按钮 -->
    <div class="monitor-actions">
      <el-button type="primary" icon="Upload" @click="showUploadDialog">上传Invoice</el-button>
      <el-button type="success" icon="Setting" @click="showConfigDialog">自定义账单配置</el-button>
      <el-button icon="Refresh" @click="refreshData">刷新</el-button>
    </div>

    <!-- 筛选栏 -->
    <el-card class="filter-card">
      <el-form :inline="true" :model="filters" size="small">
        <el-form-item label="月份" required>
          <el-date-picker
            v-model="filters.month"
            type="month"
            placeholder="选择月份"
            format="yyyy-MM"
            value-format="yyyyMM"
            :clearable="false"
          />
        </el-form-item>

        <el-form-item label="销售">
          <el-select v-model="filters.sales" :disabled="isSales" placeholder="请选择销售" clearable>
            <el-option v-for="s in salesList" :key="s" :value="s" :label="s" />
          </el-select>
        </el-form-item>

        <el-form-item label="Payer">
          <el-input v-model="filters.payer_id" placeholder="输入Payer ID" clearable style="width: 150px" />
        </el-form-item>

        <el-form-item label="Link">
          <el-input v-model="filters.link_id" placeholder="输入Link ID" clearable style="width: 150px" />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" icon="Search" @click="queryData">查询</el-button>
          <el-button icon="RefreshLeft" @click="resetFilters">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 监控数据表格 -->
    <el-card class="monitor-card">
      <template #header>
        <span>文件生成监控</span>
        <el-tag v-if="monitorData.length > 0" type="info" size="small" style="margin-left: 10px">
          共 {{ getTotalCount() }} 条记录
        </el-tag>
      </template>

      <el-table
        v-loading="loading"
        :data="monitorData"
        row-key="getRowKey"
        :tree-props="{ children: 'children' }"
        stripe
        style="width: 100%"
      >
        <el-table-column prop="name" label="名称" min-width="200">
          <template #default="{ row }">
            <span v-if="row.type === 'sales'" style="font-weight: bold; color: #409EFF">
              销售: {{ row.name }}
            </span>
            <span v-else-if="row.type === 'payer'" style="font-weight: bold; color: #E6A23C">
              Payer: {{ row.payer_name }} ({{ row.payer_id }})
            </span>
            <span v-else-if="row.type === 'link'" style="font-weight: bold">
              Link: {{ row.link_id }}
              <el-tag size="small" :type="row.region === 'cn' ? 'warning' : 'success'" style="margin-left: 5px">
                {{ row.region === 'cn' ? '中国区' : '海外区' }}
              </el-tag>
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Excel账单" width="120" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.type === 'link'" :type="row.excel_complete ? 'success' : 'warning'" size="small">
              {{ row.excel_progress }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="PDF账单" width="120" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.type === 'link'" :type="row.pdf_complete ? 'success' : 'warning'" size="small">
              {{ row.pdf_progress }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="对账单" width="120" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.type === 'link'" :type="row.detail_complete ? 'success' : 'warning'" size="small">
              {{ row.detail_progress }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="整合表" width="120" align="center">
          <template #default="{ row }">
            <el-tag v-if="row.type === 'link'" :type="row.integration_complete ? 'success' : 'warning'" size="small">
              {{ row.integration_progress }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="150" align="center">
          <template #default="{ row }">
            <el-button
              v-if="row.type === 'link'"
              type="primary"
              size="small"
              @click="regenerate(row)"
            >
              重新生成
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- 上传Invoice弹窗 -->
    <el-dialog v-model="uploadDialogVisible" title="上传Invoice" width="800px">
      <invoice-upload-content @success="handleUploadSuccess" />
    </el-dialog>

    <!-- 配置弹窗 -->
    <el-dialog v-model="configDialogVisible" title="自定义账单配置" width="1000px">
      <bill-config-content />
    </el-dialog>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from 'element-plus'
import InvoiceUploadContent from './InvoiceUploadContent.vue'
import BillConfigContent from './BillConfigContent.vue'

export default {
  name: 'InvoiceMonitorTab',
  components: {
    InvoiceUploadContent,
    BillConfigContent
  },
  data() {
    const lastMonth = new Date()
    lastMonth.setMonth(lastMonth.getMonth() - 1)
    const defaultMonth = lastMonth.getFullYear() + String(lastMonth.getMonth() + 1).padStart(2, '0')

    return {
      filters: {
        month: defaultMonth,
        sales: '',
        payer_id: '',
        link_id: ''
      },
      monitorData: [],
      salesList: [],
      loading: false,
      uploadDialogVisible: false,
      configDialogVisible: false,
      isSales: false
    }
  },
  mounted() {
    this.checkUserRole()
    this.loadSalesList()
    this.queryData()
  },
  methods: {
    checkUserRole() {
      const curRole = localStorage.getItem('curRole')
      this.isSales = curRole === 'sales'
      if (this.isSales) {
        this.filters.sales = localStorage.getItem('account')
      }
    },

    async loadSalesList() {
      try {
        const response = await this.$http.get('/bill/sales-list/')
        if (response.data.status === 'success') {
          this.salesList = response.data.data
        }
      } catch (error) {
        console.error('加载销售列表失败:', error)
      }
    },

    async queryData() {
      if (!this.filters.month) {
        ElMessage.warning('请选择月份')
        return
      }

      this.loading = true
      try {
        const params = {
          month: this.filters.month,
          sales: this.filters.sales,
          payer_id: this.filters.payer_id,
          link_id: this.filters.link_id
        }

        const response = await this.$http.get('/bill/invoice-monitor/', { params })

        if (response.data.status === 'success') {
          this.monitorData = response.data.data
          ElMessage.success('查询成功')
        } else {
          ElMessage.error(response.data.message || '查询失败')
        }
      } catch (error) {
        ElMessage.error('查询失败: ' + (error.response?.data?.message || error.message))
      } finally {
        this.loading = false
      }
    },

    resetFilters() {
      const lastMonth = new Date()
      lastMonth.setMonth(lastMonth.getMonth() - 1)
      const defaultMonth = lastMonth.getFullYear() + String(lastMonth.getMonth() + 1).padStart(2, '0')

      this.filters = {
        month: defaultMonth,
        sales: this.isSales ? localStorage.getItem('account') : '',
        payer_id: '',
        link_id: ''
      }
      this.queryData()
    },

    refreshData() {
      this.queryData()
    },

    showUploadDialog() {
      this.uploadDialogVisible = true
    },

    showConfigDialog() {
      this.configDialogVisible = true
    },

    handleUploadSuccess() {
      this.uploadDialogVisible = false
      ElMessage.success('上传成功，正在生成文件...')
      setTimeout(() => {
        this.queryData()
      }, 2000)
    },

    async regenerate(row) {
      try {
        await ElMessageBox.confirm(
          `确定要重新生成 Link ${row.link_id} 的所有文件吗？`,
          '确认重新生成',
          {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
          }
        )

        const response = await this.$http.post('/bill/regenerate-by-link/', {
          link_id: row.link_id,
          payer_id: row.payer_id,
          month: row.month
        })

        if (response.data.status === 'success') {
          ElMessage.success(response.data.message)
          setTimeout(() => {
            this.queryData()
          }, 3000)
        } else {
          ElMessage.error(response.data.message || '启动失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error('操作失败: ' + (error.response?.data?.message || error.message))
        }
      }
    },

    getRowKey(row) {
      if (row.type === 'sales') return `sales-${row.name}`
      if (row.type === 'payer') return `payer-${row.payer_id}`
      if (row.type === 'link') return `link-${row.link_id}`
      return Math.random().toString()
    },

    getTotalCount() {
      let count = 0
      const countLinks = (nodes) => {
        nodes.forEach(node => {
          if (node.type === 'link') count++
          if (node.children) countLinks(node.children)
        })
      }
      countLinks(this.monitorData)
      return count
    }
  }
}
</script>

<style scoped>
.invoice-monitor-tab {
  padding: 20px;
}

.monitor-actions {
  margin-bottom: 20px;
}

.filter-card {
  margin-bottom: 20px;
}

.monitor-card {
  min-height: 400px;
}
</style>
